import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AllocationPayload, agirNaTarefaDeAcesso, alocacoesDaPessoa, alocar, atualizarAlocacao, atualizarCobertura, clientesParaAlocacao,
  coberturasDasFerias, darCiencia, encerrarAlocacao, feriasDaEquipeDoCliente, manifestarConflito, opcoesDeAlocacao, tarefasDeAcesso,
} from '../api/alocacoes';

export const ALOC_KEYS = {
  all: ['pessoas', 'alocacoes'] as const,
  pessoa: (id: string) => ['pessoas', 'alocacoes', 'pessoa', id] as const,
  clientes: ['pessoas', 'alocacoes', 'clientes'] as const,
  opcoes: (clientId: string) => ['pessoas', 'alocacoes', 'opcoes', clientId] as const,
  coberturas: (requestId: string) => ['pessoas', 'alocacoes', 'coberturas', requestId] as const,
  tarefas: (scope: string) => ['pessoas', 'alocacoes', 'tarefas', scope] as const,
  portal: ['portal-cliente', 'ferias'] as const,
};

function useWrite<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>, extra: readonly unknown[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ALOC_KEYS.all });
      qc.invalidateQueries({ queryKey: ['pessoas', 'ferias'] });
      if (extra.length) qc.invalidateQueries({ queryKey: extra });
    },
  });
}

export const useAlocacoes = (personId: string, enabled = true) =>
  useQuery({ queryKey: ALOC_KEYS.pessoa(personId), queryFn: () => alocacoesDaPessoa(personId), enabled });
export const useClientesParaAlocacao = (enabled = true) =>
  useQuery({ queryKey: ALOC_KEYS.clientes, queryFn: clientesParaAlocacao, enabled, staleTime: 5 * 60_000 });
export const useOpcoesDeAlocacao = (clientId: string) =>
  useQuery({ queryKey: ALOC_KEYS.opcoes(clientId), queryFn: () => opcoesDeAlocacao(clientId), enabled: !!clientId });
export const useAlocar = (bondId: string) => useWrite((p: AllocationPayload) => alocar(bondId, p));
export const useAtualizarAlocacao = () => useWrite(({ id, payload }: { id: string; payload: AllocationPayload }) => atualizarAlocacao(id, payload));
export const useEncerrarAlocacao = () =>
  useWrite(({ id, validTo, reason }: { id: string; validTo: string; reason: string }) => encerrarAlocacao(id, validTo, reason));

export const useCoberturas = (requestId: string, enabled = true) =>
  useQuery({ queryKey: ALOC_KEYS.coberturas(requestId), queryFn: () => coberturasDasFerias(requestId), enabled });
export const useAtualizarCobertura = () =>
  useWrite(({ id, payload }: { id: string; payload: { needsSubstitute?: boolean; substituteUserId?: string | null } }) =>
    atualizarCobertura(id, payload));

export const useTarefasDeAcesso = (scope: 'abertas' | 'todas') =>
  useQuery({ queryKey: ALOC_KEYS.tarefas(scope), queryFn: () => tarefasDeAcesso(scope) });
export const useAgirNaTarefaDeAcesso = () =>
  useWrite(({ id, action, notes }: { id: string; action: 'concluir' | 'falha' | 'excecao'; notes?: string }) =>
    agirNaTarefaDeAcesso(id, action, notes));

export const useFeriasDaEquipeDoCliente = () => useQuery({ queryKey: ALOC_KEYS.portal, queryFn: feriasDaEquipeDoCliente });
export const useDarCiencia = () => useWrite((coverageId: string) => darCiencia(coverageId), ALOC_KEYS.portal);
export const useManifestarConflito = () =>
  useWrite(({ coverageId, justification }: { coverageId: string; justification: string }) => manifestarConflito(coverageId, justification), ALOC_KEYS.portal);
